import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export type AttachmentType = 
  | "youtube" 
  | "link" 
  | "upload" 
  | "file"
  | "drive" 
  | "doc" 
  | "slide" 
  | "sheet" 
  | "form" 
  | "drawing" 
  | "vids"
  | "video";

export interface TopicAttachment {
  id: string;
  type: AttachmentType;
  title: string;
  url: string;
  file_size?: string;
  file_type?: string;
  thumbnail?: string;
}

export interface LessonData {
  id?: number | string;
  module: number | string;
  title: string;
  duration?: string;
  content?: string;
  video_url?: string;
  attachments?: TopicAttachment[];
  order?: number;
  is_approved?: boolean;
  pending_deletion?: boolean;
  date_created?: string;
}

export interface ExerciseData {
  id?: number | string;
  module: number | string;
  title: string;
  exercise_task: string;
  instructor_feedback?: string;
  document_upload?: string;
  exercise_deadline?: string;
  is_completed?: boolean;
  is_approved?: boolean;
  pending_deletion?: boolean;
  requires_autograder?: boolean;
  autograder_template_repo?: string;
  autograder_module_name?: string;
  submission?: {
    id: number;
    repository_name: string;
    repository_url: string;
    github_username?: string;
    latest_commit_sha?: string;
    score: number;
    passed_tests: number;
    total_tests: number;
    module_scores?: Record<string, number>;
    is_passed: boolean;
    module_name?: string | null;
    module_score?: number | null;
    module_passed?: boolean | null;
    created_at?: string;
    updated_at?: string;
  } | null;
  date?: string;
}

export interface ModuleData {
  id?: number | string;
  course: number | string;
  title: string;
  order?: number;
  lesson_order?: (number | string)[];
  date_created?: string;
  is_approved?: boolean;
  pending_deletion?: boolean;
  lessons?: LessonData[];
  exercise_module?: ExerciseData[];
}

export interface InstructorData {
  id: number | string;
  name: string;
  bio?: string;
  get_photo_url?: string;
  rating?: number | string;
  students_count?: string;
}

export interface PresenceData {
  id: number;
  name: string;
}

export interface CourseData {
  id?: number | string;
  title: string;
  slug?: string;
  description: string;
  requirement?: string;
  outline?: string;
  outline_snippet?: string;
  why_choose_course?: string;
  payment_type?: "Paid" | "Free" | string;
  level?: "Beginner" | "Intermidiate" | "Advance" | string;
  online_price: number | string;
  onsite_price: number | string;
  course_img_url?: string;
  duration?: string;
  rating?: string | number;
  review_count?: string;
  enrolled_count?: string;
  lessons_count?: number;
  has_certificate?: boolean;
  has_lifetime_access?: boolean;
  has_mentor_support?: boolean;
  has_digital_assets?: boolean;
  autograder_template_repo?: string;
  module_order?: (number | string)[];
  track_ids?: (number | string)[];
  presence_ids?: number[];
  presence?: PresenceData[];
  approval_status?: "APPROVED" | "PENDING" | "REJECTED" | string;
  is_live?: boolean;
  modules?: ModuleData[];
  instructor?: any;
  instructor_name?: string;
}

export interface TrackData {
  id?: number | string;
  title: string;
  slug?: string;
  description: string;
  requirement?: string;
  outline?: string;
  outline_snippet?: string;
  why_choose_track?: string;
  payment_type?: "Paid" | "Free" | string;
  level?: "Beginner" | "Intermidiate" | "Advance" | string;
  online_price: number | string;
  onsite_price: number | string;
  track_img_url?: string;
  duration?: string;
  rating?: string | number;
  review_count?: string;
  enrolled_count?: string;
  lessons_count?: number;
  has_certificate?: boolean;
  has_lifetime_access?: boolean;
  has_mentor_support?: boolean;
  has_digital_assets?: boolean;
  approval_status?: "APPROVED" | "PENDING" | "REJECTED" | string;
  is_live?: boolean;
  course_ids?: (number | string)[];
  course_order?: (number | string)[];
  presence_ids?: number[];
  presence?: PresenceData[];
  course_in_track?: CourseData[];
  instructor?: any;
  instructor_name?: string;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized yet
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

// ================= Instructors & Presences =================
export const fetchInstructorsList = async (): Promise<InstructorData[]> => {
  const client = getClient();
  const res = await client.get("/course/instructors/");
  return Array.isArray(res.data) ? res.data : [];
};

export const fetchCoursePresences = async (): Promise<PresenceData[]> => {
  const client = getClient();
  const res = await client.get("/course/presences/");
  return Array.isArray(res.data) ? res.data : [];
};

// ================= Courses =================
export const fetchCoursesList = async (): Promise<CourseData[]> => {
  const client = getClient();
  const res = await client.get("/course/bk-course");
  return Array.isArray(res.data) ? res.data : [];
};

export const fetchCourseDetail = async (idOrSlug: string | number): Promise<CourseData> => {
  const client = getClient();
  const res = await client.get(`/course/bk-course/${idOrSlug}/`);
  return res.data;
};

export const createCourse = async (data: Partial<CourseData>): Promise<CourseData> => {
  const client = getClient();
  const res = await client.post("/course/bk-course/", data);
  return res.data;
};

export const updateCourse = async (id: number | string, data: Partial<CourseData>): Promise<CourseData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-course/${id}/`, data);
  return res.data;
};

export const deleteCourse = async (id: number | string): Promise<void> => {
  const client = getClient();
  await client.delete(`/course/bk-course/${id}/`);
};

// ================= Tracks =================
export const fetchTracksList = async (): Promise<TrackData[]> => {
  const client = getClient();
  const res = await client.get("/course/bk-track");
  return Array.isArray(res.data) ? res.data : [];
};

export const fetchTrackDetail = async (idOrSlug: string | number): Promise<TrackData> => {
  const client = getClient();
  const res = await client.get(`/course/bk-track/${idOrSlug}/`);
  return res.data;
};

export const createTrack = async (data: Partial<TrackData>): Promise<TrackData> => {
  const client = getClient();
  const res = await client.post("/course/bk-track/", data);
  return res.data;
};

export const updateTrack = async (id: number | string, data: Partial<TrackData>): Promise<TrackData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-track/${id}/`, data);
  return res.data;
};

export const deleteTrack = async (id: number | string): Promise<void> => {
  const client = getClient();
  await client.delete(`/course/bk-track/${id}/`);
};

export const reorderTrackCourses = async (
  trackId: number | string,
  courseOrder: (number | string)[]
): Promise<TrackData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-track/${trackId}/`, {
    course_order: courseOrder,
  });
  return res.data;
};

// ================= Modules =================
export const fetchModules = async (courseId?: number | string): Promise<ModuleData[]> => {
  const client = getClient();
  const params = courseId ? { course_id: courseId } : {};
  const res = await client.get("/course/bk-modules/", { params });
  return Array.isArray(res.data) ? res.data : [];
};

export const createModule = async (data: { course: number | string; title: string }): Promise<ModuleData> => {
  const client = getClient();
  const res = await client.post("/course/bk-modules/", data);
  return res.data;
};

export const updateModule = async (id: number | string, data: { title?: string; course?: number | string; lesson_order?: (number | string)[] }): Promise<ModuleData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-modules/${id}/`, data);
  return res.data;
};

export const deleteModule = async (id: number | string): Promise<void> => {
  const client = getClient();
  await client.delete(`/course/bk-modules/${id}/`);
};

export const reorderCourseModules = async (
  courseId: number | string,
  moduleOrder: (number | string)[]
): Promise<CourseData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-course/${courseId}/`, {
    module_order: moduleOrder.map(Number),
  });
  return res.data;
};

// ================= Lessons (Topics) =================
export const fetchLessons = async (moduleId?: number | string): Promise<LessonData[]> => {
  const client = getClient();
  const params = moduleId ? { module_id: moduleId } : {};
  const res = await client.get("/course/bk-lessons/", { params });
  return Array.isArray(res.data) ? res.data : [];
};

export const createLesson = async (data: Partial<LessonData> & { module: number | string; title: string }): Promise<LessonData> => {
  const client = getClient();
  const res = await client.post("/course/bk-lessons/", data);
  return res.data;
};

export const updateLesson = async (id: number | string, data: Partial<LessonData>): Promise<LessonData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-lessons/${id}/`, data);
  return res.data;
};

export const deleteLesson = async (id: number | string): Promise<void> => {
  const client = getClient();
  await client.delete(`/course/bk-lessons/${id}/`);
};

export const reorderModuleLessons = async (
  moduleId: number | string,
  lessonOrder: (number | string)[]
): Promise<ModuleData> => {
  const client = getClient();
  const res = await client.patch(`/course/bk-modules/${moduleId}/`, {
    lesson_order: lessonOrder.map(Number),
  });
  return res.data;
};

// ================= Exercises (Tasks) =================
export const fetchExercises = async (moduleId?: number | string): Promise<ExerciseData[]> => {
  const client = getClient();
  const params = moduleId ? { module_id: moduleId } : {};
  const res = await client.get("/course/bk-exercises/", { params });
  return Array.isArray(res.data) ? res.data : [];
};

const sanitizeExercisePayload = (data: Record<string, any>) => {
  const payload = { ...data };
  if (!payload.exercise_deadline) {
    delete payload.exercise_deadline;
  }
  if (!payload.document_upload) {
    delete payload.document_upload;
  }
  if (!payload.requires_autograder) {
    payload.requires_autograder = false;
    payload.autograder_template_repo = "";
    payload.autograder_module_name = "";
  }
  return payload;
};

export const createExercise = async (data: Partial<ExerciseData> & { module: number | string; title: string; exercise_task: string }): Promise<ExerciseData> => {
  const client = getClient();
  const payload = sanitizeExercisePayload(data);
  const res = await client.post("/course/bk-exercises/", payload);
  return res.data;
};

export const updateExercise = async (id: number | string, data: Partial<ExerciseData>): Promise<ExerciseData> => {
  const client = getClient();
  const payload = sanitizeExercisePayload(data);
  const res = await client.patch(`/course/bk-exercises/${id}/`, payload);
  return res.data;
};

export const deleteExercise = async (id: number | string): Promise<void> => {
  const client = getClient();
  await client.delete(`/course/bk-exercises/${id}/`);
};

// ================= Asset Upload =================
export const uploadCurriculumAsset = async (file: File): Promise<{ url: string; public_id?: string; format?: string; name?: string; bytes?: number }> => {
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  const formData = new FormData();
  formData.append("file", file);

  const res = await axios.post(`${baseURL}/course/upload-asset/`, formData, {
    headers: {
      "Content-Type": "multipart/form-data",
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
  return res.data;
};

// ================= Assigned Curriculum & Students for Instructors =================
export interface InstructorStudentSubmissionInfo {
  id: number;
  repository_name: string;
  repository_url: string;
  score: number;
  passed_tests: number;
  total_tests: number;
  is_passed: boolean;
}

export interface InstructorStudentItem {
  id: string;
  enrollment_id: number;
  student_id: number | null;
  name: string;
  email: string;
  phone_number?: string;
  github_username?: string;
  avatar: string;
  course: string;
  curriculum_id: number;
  curriculum_type: "course" | "track";
  delivery_mode: "online" | "onsite";
  is_paid: boolean;
  is_enrolled: boolean;
  progress: number;
  completed_lessons_count: number;
  total_lessons_count: number;
  topics_count: number;
  tasks_count: number;
  lastActive: string;
  lastActiveTimestamp: number;
  joined: string;
  submission?: InstructorStudentSubmissionInfo | null;
}

export interface InstructorCurriculumOption {
  id: number;
  title: string;
  slug: string;
  type: "course" | "track";
  lessons_count: number;
  topics_count: number;
  tasks_count: number;
  rating: number;
  review_count: string;
  enrolled_count: string;
}

export interface InstructorStudentsResponse {
  students: InstructorStudentItem[];
  courses: InstructorCurriculumOption[];
  kpis: {
    total_students: number;
    total_enrollments: number;
    completed_count: number;
    in_progress_count: number;
    average_progress: number;
    active_courses_count: number;
    total_lessons_count: number;
    avg_rating: number;
    submissions_count: number;
  };
}

export interface InstructorStudentMessagePayload {
  subject: string;
  message: string;
  recipient_student_id?: number | null;
  recipient_email?: string;
  audience?: string;
}

export const fetchInstructorStudents = async (scope?: string): Promise<InstructorStudentsResponse> => {
  const client = getClient();
  const params = scope ? { scope } : {};
  const res = await client.get("/course/instructor-students/", { params });
  return res.data;
};

export const sendInstructorStudentMessage = async (
  payload: InstructorStudentMessagePayload
): Promise<{ status: string; sent_count: number; message: string }> => {
  const client = getClient();
  const res = await client.post("/course/instructor-students/message/", payload);
  return res.data;
};

export const fetchAssignedCourses = async (): Promise<CourseData[]> => {
  const client = getClient();
  const res = await client.get("/course/bk-course/?assigned=true");
  return Array.isArray(res.data) ? res.data : [];
};

export const fetchAssignedTracks = async (): Promise<TrackData[]> => {
  const client = getClient();
  const res = await client.get("/course/bk-track/?assigned=true");
  return Array.isArray(res.data) ? res.data : [];
};

// ================= Content Approval Workflow =================
export const approveContentItem = async (
  itemType: "module" | "lesson" | "exercise",
  itemId: number | string,
  action: "approve" | "confirm_delete" | "restore"
): Promise<{ status: string; action: string; item_id: number | string; has_pending: boolean }> => {
  const client = getClient();
  const res = await client.post("/course/approve-content/", {
    item_type: itemType,
    item_id: itemId,
    action,
  });
  return res.data;
};

export const approveAllCourseContent = async (
  courseId: number | string
): Promise<{ status: string; message: string }> => {
  const client = getClient();
  const res = await client.post(`/course/courses/${courseId}/approve-all/`);
  return res.data;
};

// ================= Google Gemini Pro Curriculum Copilot (SuperAdmin) =================
export interface AISyllabusLesson {
  title: string;
  duration: string;
  content: string;
}

export interface AISyllabusExercise {
  title: string;
  exercise_task: string;
  requires_autograder: boolean;
  autograder_module_name: string;
}

export interface AISyllabusModule {
  title: string;
  lessons: AISyllabusLesson[];
  exercises: AISyllabusExercise[];
}

export interface AIGenerateRequest {
  action: "identity" | "syllabus" | "lesson" | "exercise";
  api_key?: string;
  model?: string;
  title?: string;
  course_title?: string;
  module_title?: string;
  module_index?: number;
  lesson_title?: string;
  exercise_title?: string;
  target_file?: string;
  target_file_hint?: string;
  level?: string;
  duration?: string;
  item_type?: "course" | "track";
  description?: string;
  module_count?: number;
  lessons_per_module?: number;
  topics_per_module?: number;
  include_exercises?: boolean;
  include_autograder?: boolean;
  instructions?: string;
  extra_instructions?: string;
  custom_prompt?: string;
  system_prompt?: string;
  prompt_mode?: "augment" | "override";
  content_action?: "replace" | "append" | "refine";
  existing_content?: string | Record<string, string>;
  existing_modules?: Array<{ title: string; lessons?: string[] }>;
}

export const generateAICurriculumContent = async <T = any>(
  payload: AIGenerateRequest
): Promise<{ status: string; action: string; data: T }> => {
  const client = getClient();
  const res = await client.post("/course/ai/generate/", payload);
  return res.data;
};

export const bulkApplyAISyllabus = async (
  courseId: number | string,
  payload:
    | AISyllabusModule[]
    | { modules: AISyllabusModule[]; replace_existing?: boolean }
): Promise<{
  status: string;
  course_id: number;
  created_modules_count: number;
  modules: ModuleData[];
}> => {
  const client = getClient();
  const body = Array.isArray(payload) ? { modules: payload } : payload;
  const res = await client.post(`/course/courses/${courseId}/ai-bulk-apply/`, body);
  return res.data;
};
